import * as yup from 'yup';
import moment from 'moment';
import createSingularContainer from '@components/Singular/createSingularContainer'

import { CandidateModel } from '@modules/user/user.module'

CandidateModel.startFieldBuffer();

const formConfig = [
	{
		label: "System details",
		fields: CandidateModel.getFieldProps([
			'profileImage',
			'status',
			'jobTypes',
			'payrollNo',
		]),
	},
	{
		label: 'About',
		fields: CandidateModel.getFieldProps([
			'jobsApplyingFor',
			'name',
			'dateOfBirth',
			'gender',
		])
	},
	{
		label: 'Contact details',
		fields: CandidateModel.getFieldProps([
			'mobile',
			'telephone',
			'email',
		]),
	},
	{
		label: 'Emergency contact details',
		fields: CandidateModel.getFieldProps([
			'emergencyContact',
		])
	},
	{
		label: 'Right to Work and Pre-employment screening',
		fields: CandidateModel.getFieldProps([
			'immigrationPermission',
			// National Identity Card
			'nationalIdentityCardAvailable',
			'nationalIdentityCardNumber',
			'nationalIdentityCardExpiry',
			// Passport
			'passportAvailable',
			'passportID',
			'passportExpiry',
			// Visa
			'visaAvailable',
			'visaID',
			'visaExpiry',
			// BRP
			'BRPAvailable',
			'BRPID',
			'BRPExpiry',
			// UK Birth Certificate
			'birthCertificateAvailable',
			'birthCertificateID',
			'nationality',
			'countryOfBirth',
			'townCityOfBirth',
			'regionOfBirth',
			'mothersMaidenName',
			'nationalityChangedSinceBirth',
			'birthNationality',
			'dateNationalityChanged',
			'surnameChangedSinceBirth',
			'previousSurname',
			'dateSurnameChanged',
			'drivingLicenceAvailable',
			'drivingLicenceNumber',
			'SIALicenceAvailable',
			'SIALicenceType',
			'foodSafetyCertificateAvailable',
			'foodSafetyCertificateLevel',
			'hasUnspentConvictions',
			'convictionsDescription',
		])
	},
	{
		label: 'Interview Results',
		fields: CandidateModel.getFieldProps([
			'interviewResults',
			'pensionOptOutDiscussed',
			'settlementApplication',
			'referencesRequested',
			'referenceNotes',
			'referAFriendDiscussed',
		]),
	},
	{
		label: 'Address History',
		fields: CandidateModel.getFieldProps([
			'addressHistory',
		])
	},
	{
		label: 'Bank details',
		fields: CandidateModel.getFieldProps([
			'paymentDetails',
		])
	},
	{		
		label: 'Employment History',
		fields: CandidateModel.getFieldProps([
			'employmentHistory',
		]),
	},
	{
		label: 'Referee Consent',
		fields: CandidateModel.getFieldProps([
			'refereeConsent',
		])
	},
	{
		label: 'Employment Scheme',
		fields: CandidateModel.getFieldProps([
			'employmentScheme',
			'NINumberAvailable',
			'NINumber',
			'UTRNumber',
		]),
	},
	{
		label: 'Previous Agencies',
		fields: CandidateModel.getFieldProps([
			'previousAgencies',
		]),	
	},
	{
		label: 'Working Hours',
		fields: CandidateModel.getFieldProps([
			'availability',
		]),
	},
	{
		label: "Opt-out of 48 hour working week agreement",
		fields: CandidateModel.getFieldProps([
			'optOut48HourWorkingWeek'
		]),
	},
	{
		label: 'Health & Disability',
		fields: CandidateModel.getFieldProps([
			'hasHealthIssues',
			'healthIssuesDescription',
			'assistanceRequired',
		])
	},
	{
		label: "Consent",
		fields: CandidateModel.getFieldProps([
			'equalOpportunitiesAgreement',
			'dataProtectionAgreementPart1',
			'dataProtectionAgreementPart2',
			'candidateSigned',
		])
	},
	{
		label: "Documents",
		fields: CandidateModel.getFieldProps([
			['documents', {
				label: ''
			}],
		]),
	},
	{
		label: "Account",
		fields: CandidateModel.getFieldProps([
			'heardAboutHumanOne',
			['password', {
				show: ({ isEditing }) => isEditing,
			}],
			'passwordConfirm',
		]),
	}

];

const fieldPaths = CandidateModel.endFieldBuffer();
const _cachedSchemas = {};

export default createSingularContainer({
	title: (data) => data && data.name ? `${data.name.first} ${data.name.last}` : '',
	resource: CandidateModel.resourceKey,
	supportsNotes: true,
	supportsDeleting: true,	
	defaultValues: CandidateModel.getDefaults(),
	formConfig,
	validationSchema: ({ isNew }) => {
		// TODO: add auto caching to validation schema based on repeat args? OR make a cache proxy 
		const newState = isNew.toString();
		if(!_cachedSchemas[newState]) {
			// TODO: set includePrivate based on logged in user type
			_cachedSchemas[newState] = CandidateModel.getValidationSchema(fieldPaths, { isNew, includePrivate: true, strictMode: false });
		}
		return _cachedSchemas[newState];
	},
	createdString: 'Joined',
	displayAsAccordion: true,
	mapDataToCSV: (data) => {

		const dateFormat = 'YYYYMMDD';

		// Make nested objects read safe
		const referee = data.referee || {};
		const name = data.name || {};
		const paymentDetails = data.paymentDetails || {};
		const emergencyContact = data.emergencyContact || {};
		const employmentHistory = data.employmentHistory || [];
		const status = data.status || {};
		const addressHistory = data.addressHistory || [];
		const availability = data.availability || [];

		const days = {
			monday: 'Monday',
			tuesday: 'Tuesday',
			wednesday: 'Wednesday',
			thursday: 'Thursday',
			friday: 'Friday',
			saturday: 'Saturday',
			sunday: 'Sunday',
		};

		const timeFrames = {
			from: 'From',
			to: 'To',
		}

		const addressHistoryRows = addressHistory.reduce((arr, history, idx) => {
			// if(!history) return arr;
			const prefix = !idx ? 'Current Address' : `Address ${idx}`
			return [
				...arr,
				[`${prefix} line 1`, history.line1],
				[`${prefix} line 2`, history.line2],
				[`${prefix} Town / City`, history.townCity],
				[`${prefix} Country`, history.country],
				[`${prefix} Postcode`, history.postCode],
				[`${prefix} Date moved in`, history.from],
				[`${prefix} Date moved out`, history.to],
			]
		}, []);

		const availabilityRows = Object.entries(days).reduce((arr, [dayKey, dayLabel]) => {
			const available = availability[dayKey] || [];

			const shiftRows = available.reduce((shiftRows, shift, idx) => {
				const num = idx+1;
				return [
					...shiftRows, 
					[`${dayLabel} From ${num}`, data.from],
					[`${dayLabel} To ${num}`, data.to],
				]
			}, []);	

			return [...arr, ...shiftRows]
		} ,[])

		var output = [
		  ['Candidate Status', status.label],
			['Title', name.title],
			['First name', name.first],
			['Last name', name.last],
			['Date of Birth', data.dateOfBirth],
			['Gender', data.gender],
			['Nationality', data.nationality],
			// TODO: add remaining candidate fields to CSV
			['Country of Birth', data.countryOfBirth],
			['Region of Birth', data.regionOfBirth],
			['Town/City of Birth', data.townCityOfBirth],
			['National Insurance Number', data.NINumber],
			['Mother\'s Maiden Name', data.mothersMaidenName],
			['Nationality changed since birth?', data.nationalityChangedSinceBirth ? 'Yes' : 'No'],
			['Birth Nationality', data.birthNationality],
			['Surname changed since birth?', data.surnameChangedSinceBirth ? 'Yes' : 'No'],
			['Previous Surname', data.previousSurname],
			['Driving Licence available?', data.drivingLicenceAvailable ? 'Yes' : 'No'],
			['Do you have a National Identity Card?', data.nationalIdentityCardAvailable ? 'Yes' : 'No'],
			[],
			...addressHistoryRows,
			['Phone', data.telephone],
			['Mobile', data.mobile],
			['Email', data.email],
			[],
			['Emergency contact name', emergencyContact.name],
			['Relationship', emergencyContact.relationship],
			['Their home telephone number (optional)', emergencyContact.telephone],
			['Their mobile number', emergencyContact.mobile],
			[],
			['Employment Scheme', data.employmentScheme],
			['UTR Number', data.UTRNumber],
			[],
		];

		// employment history
		employmentHistory.forEach((o, idx) => {
			const num = idx+1;
			output.concat([
				[`Previous Employer ${num} Name`, o.companyName],
				[`Previous Employer ${num} Address 1`, o.companyAddress.line1],
				[`Previous Employer ${num} Address 2`, o.companyAddress.line2],
				[`Previous Employer ${num} Town / City`, o.companyAddress.townCity],
				[`Previous Employer ${num} County`, o.companyAddress.line1],
				[`Previous Employer ${num} Postcode`, o.companyAddress.line1],
				[`Previous Employer ${num} Position Held`, o.position ],
				[`Previous Employer ${num} From Date`, moment(o.datesWorked.from).format(dateFormat)],
				[`Previous Employer ${num} To Date`, moment(o.datesWorked.to).format(dateFormat)],
				[`Previous Employer ${num} Duties`, o.duties],
				[`Previous Employer ${num} Reason(s) For Leaving`, o.reasonsForLeaving],
			])
		});

		output = output.concat([
			['Referee name', referee.name],
			['Referee position', referee.position],
			['Referee telephone number', referee.telephone],
			['Referee email address', referee.email],
			[],
			['I hereby give Human One my permission to contact the above named referees.', referee.permission ? 'Yes' : 'No'],
			[],
			['What other agencies have you worked with and where did they send you?', data.previousAgencies],
			[],
			...availabilityRows,
		]);
			
		output = output.concat([
			['Do you have immigration permission to work in the UK?', data.immigrationPermission ? 'Yes' : 'No' ],
			['Do you have any unspent criminal convictions?', data.hasUnspentConvictions ? 'Yes' : 'No' ],
			['Detail conviction(s) and date(s)', data.convictionsDescription],
			[],
			['I wish to opt-out of the 48 Hour Working Week Agreement', data.optOut48HourWorkingWeek ? 'Yes' : 'No'],
			[],
			['Do you have any health issues of disability relevant which make it difficult for you to carry out functions which are essential for the role you seek?', data.hasHealthIssues ? 'Yes' : 'No'],
			[],
			['If you have a disability, what are your needs in terms of reasonable adjustments in order to access this recruitment service and to attend interview, or to take aptitude tests etc?', data.assistanceRequired ],
			[],
			['Bank / Building Society Name', paymentDetails.bankName],
			['Building Society Roll Number', paymentDetails.rollNumber],
			['Account Holder Name', paymentDetails.accountHolderName],
			['Sort Code', paymentDetails.sortCode],
			['Account Number', paymentDetails.accountNumber],
			[],
			['How did you hear about us?', data.heardAboutHumanOne],
			[],
			['Candidate declaration signed', data.candidateSigned],
		]);

		return output;
	},
	mapDataToCSVFilename: (data) => {
		return `h1export-candidate-${data.name.first.replace(/\s+/g, '-').toLowerCase()}-${moment().format('YYYYMMDD')}.csv`},
});




// WEBPACK FOOTER //
// ./src/scenes/Candidates/CandidateContainer.jsx